import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { CheckCircle2, MessageCircle, Send, Star } from 'lucide-react';
import { AvatarStack } from '../ui/avatar-stack';
import { climbUrl } from '../../utils/climb-url';
import { climbHeading, climbTitle } from '../../utils/climb-title';
import { ClimbSendersDrawer } from './instagram/ClimbSendersDrawer';
import { ClimbRatersDrawer } from './instagram/ClimbRatersDrawer';
import { ClimbCommentsDrawer } from './instagram/ClimbCommentsDrawer';
import { HoldsOverlay } from '../climbs/HoldsOverlay';
import { RouteLineOverlay } from '../climbs/RouteLineOverlay';
import { useHoldsDisplayMode } from '../climbs/holds-display-mode';
import { routeLineDisplay, useRouteLineDisplayMode } from '../climbs/route-line-display-mode';
import { hasCorridor, isDrawableLine } from '../climbs/route-line';
import { climbShareUrl } from '../climbs/climb-photo-effect';
import type { Climb } from '../../types/climb';

interface ClimbInstagramCardProps {
  climb: Climb;
}

export function ClimbInstagramCard({ climb }: ClimbInstagramCardProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dateLocale = i18n.language === 'it' ? it : enUS;

  const [sendsOpen, setSendsOpen] = useState(false);
  const [ratersOpen, setRatersOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [optimisticCommentDelta, setOptimisticCommentDelta] = useState(0);
  const [imageDims, setImageDims] = useState<{ width: number; height: number } | null>(null);
  // Reflect the persisted display mode; circles are shown statically (no animation).
  const { mode: holdsMode } = useHoldsDisplayMode();
  const { mode: lineMode } = useRouteLineDisplayMode();
  // A route drawn as a line follows the line's mode, holds included — the feed
  // has no toggle of its own, so it reads whatever was last set on a climb.
  const hasLine = isDrawableLine(climb.line);
  const lineDisplay = routeLineDisplay(lineMode, hasCorridor(climb.line));
  const cardHoldsMode = hasLine ? (lineDisplay.line ? 'all' : 'none') : holdsMode;

  const sendsCount = climb.sends_count ?? 0;
  const commentsCount = (climb.comments_count ?? 0) + optimisticCommentDelta;
  const senders = climb.senders_preview ?? [];

  const handleShare = async () => {
    const url = climbShareUrl(`${window.location.origin}${climbUrl(climb)}`, climb);
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await navigator.share({ url, title: climbTitle(climb) });
        return;
      } catch {
        // user cancelled or unsupported — fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      // Lightweight feedback via i18n alert. UI projects without a toast system
      // can swap this for their preferred notifier.
      window.alert(t('home.instagram.linkCopied'));
    } catch {
      window.alert(t('home.instagram.shareFailed'));
    }
  };

  const ratingDisplay =
    climb.average_rating != null ? climb.average_rating.toFixed(1) : '—';

  const timeAgo = formatDistanceToNow(new Date(climb.created_at), {
    addSuffix: true,
    locale: dateLocale,
  });

  const goToClimb = () => navigate(climbUrl(climb));

  const goToGym = () => {
    if (climb.location.type === 'Gym') {
      navigate(`/gyms/${climb.location.slug}`);
    } else if (climb.location.type === 'Crag') {
      navigate(`/crags/${climb.location.slug}`);
    } else {
      navigate(`/competitions/${climb.location.slug}`);
    }
  };

  // What the climb is called on the wall — the grade, the holds, the sector —
  // with the name it was given, if it has one, under it. The grade is part of
  // the heading now, so it is not repeated beside it.
  const { heading, name } = climbHeading(climb);

  return (
    <article className="rounded-lg overflow-hidden border bg-card mx-auto w-full max-w-md md:max-w-lg">
      <div className="relative">
        {climb.image_medium_url ? (
          <>
            <img
              src={climb.image_medium_url}
              alt={climbTitle(climb)}
              className="w-full aspect-[3/4] object-cover cursor-pointer"
              onClick={goToClimb}
              onLoad={(e) => setImageDims({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
            />
            <HoldsOverlay
              holds={climb.holds}
              naturalWidth={imageDims?.width ?? null}
              naturalHeight={imageDims?.height ?? null}
              objectFit="cover"
              mode={cardHoldsMode}
              animate={false}
            />
            {hasLine && (lineDisplay.line || lineDisplay.focus) && (
              <RouteLineOverlay
                line={climb.line}
                naturalWidth={imageDims?.width ?? null}
                naturalHeight={imageDims?.height ?? null}
                objectFit="cover"
                showLine={lineDisplay.line}
                focusOutside={lineDisplay.focus}
                animate={false}
              />
            )}
          </>
        ) : (
          <button
            onClick={goToClimb}
            className="w-full aspect-[3/4] bg-muted flex items-center justify-center"
          >
            <span className="text-sm text-muted-foreground">{climbTitle(climb)}</span>
          </button>
        )}

        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent px-3 py-2 pointer-events-none">
          <p
            className="text-white text-sm font-semibold text-center truncate"
            style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}
          >
            {heading}
          </p>
          {name && (
            <p
              className="truncate text-center text-xs text-white/80"
              style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}
            >
              {name}
            </p>
          )}
        </div>

        <button
          className="absolute bottom-2 left-2 flex items-center gap-2 px-2 py-1 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            goToGym();
          }}
        >
          {climb.location.logo_small_url && (
            <img
              src={climb.location.logo_small_url}
              alt={climb.location.name}
              className="w-6 h-6 rounded-full object-cover border border-white/30"
            />
          )}
          <span
            className="text-white text-xs font-medium"
            style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}
          >
            {climb.location.name}
          </span>
        </button>

        {/* Only a setter with an account is worth tapping — a guest has no
            profile behind their face. */}
        {climb.route_setter && (() => {
          const setter = climb.route_setter;
          const face = setter.avatar_small_url ? (
            <img
              src={setter.avatar_small_url}
              alt=""
              className="w-8 h-8 rounded-full border-2 border-white shadow-md object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-muted border-2 border-white shadow-md flex items-center justify-center text-xs font-medium">
              {setter.name.charAt(0).toUpperCase()}
            </div>
          );

          return setter.slug ? (
            <button
              className="absolute bottom-2 right-2 hover:scale-110 transition-transform"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/users/${setter.slug}`);
              }}
              aria-label={setter.name}
            >
              {face}
            </button>
          ) : (
            <span className="absolute bottom-2 right-2" title={setter.name}>
              {face}
            </span>
          );
        })()}
      </div>

      <div className={`flex items-center gap-4 px-3 pt-3 ${sendsCount === 0 ? 'pb-3' : ''}`}>
        <button
          onClick={() => setSendsOpen(true)}
          className="flex items-center gap-1.5 text-sm hover:text-primary transition-colors"
          aria-label={t('home.instagram.sendsTitle')}
        >
          <CheckCircle2 className="w-5 h-5" />
          <span>{sendsCount}</span>
        </button>
        <button
          onClick={() => setRatersOpen(true)}
          className="flex items-center gap-1.5 text-sm hover:text-primary transition-colors"
          aria-label={t('home.instagram.averageRating')}
        >
          <Star className="w-5 h-5" />
          <span>{ratingDisplay}</span>
        </button>
        <button
          onClick={() => setCommentsOpen(true)}
          className="flex items-center gap-1.5 text-sm hover:text-primary transition-colors"
          aria-label={t('home.instagram.commentsTitle')}
        >
          <MessageCircle className="w-5 h-5" />
          <span>{commentsCount}</span>
        </button>
        <button
          onClick={handleShare}
          className="flex items-center gap-1.5 text-sm hover:text-primary transition-colors"
          aria-label={t('home.instagram.share')}
        >
          <Send className="w-5 h-5" />
        </button>

        <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">{timeAgo}</span>
      </div>

      {sendsCount > 0 && (
        <button
          onClick={() => setSendsOpen(true)}
          className="flex items-center gap-2 px-3 pt-2 pb-3 w-full text-left hover:bg-muted/50 transition-colors"
        >
          <AvatarStack users={senders} totalCount={sendsCount} max={3} />
          {senders.length > 0 && (
            <span className="text-xs text-muted-foreground">
              <Trans
                i18nKey="home.instagram.sentBy"
                values={{ name: senders[0].username }}
                count={sendsCount}
                components={{ strong: <strong className="font-semibold text-foreground" /> }}
              />
            </span>
          )}
        </button>
      )}

      <ClimbSendersDrawer
        open={sendsOpen}
        onOpenChange={setSendsOpen}
        climbSlugOrId={climb.slug}
      />
      <ClimbRatersDrawer
        open={ratersOpen}
        onOpenChange={setRatersOpen}
        climbSlugOrId={climb.slug}
      />
      <ClimbCommentsDrawer
        open={commentsOpen}
        onOpenChange={setCommentsOpen}
        climbSlugOrId={climb.slug}
        onCountChange={(delta) => setOptimisticCommentDelta((d) => d + delta)}
      />
    </article>
  );
}
